import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_EVENTS: Array<keyof WindowEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
];

/**
 * Client-side idle timer.
 *
 * Returns:
 * - isIdle: becomes true after `idleMs` without user activity
 * - reset: marks as active and restarts the timer (also called on activity)
 */
export function useIdleTimer(idleMs: number, events: Array<keyof WindowEventMap> = DEFAULT_EVENTS) {
  const [isIdle, setIsIdle] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const clear = () => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const start = useCallback(() => {
    clear();
    timeoutRef.current = window.setTimeout(() => {
      setIsIdle(true);
    }, idleMs);
  }, [idleMs]);

  const reset = useCallback(() => {
    setIsIdle(false);
    start();
  }, [start]);

  useEffect(() => {
    if (idleMs <= 0) {
      setIsIdle(false);
      clear();
      return;
    }

    const onActivity = () => {
      reset();
    };

    events.forEach((eventName) => window.addEventListener(eventName, onActivity, { passive: true }));
    start();

    return () => {
      clear();
      events.forEach((eventName) =>
        window.removeEventListener(eventName, onActivity as EventListener)
      );
    };
  }, [events, idleMs, reset, start]);

  return [isIdle, reset] as const;
}

export default useIdleTimer;

